// The dashboard's map (components/DashMap.svelte): the tone of each row,
// and the overview across the top of the page that pictures the whole
// list with them. Each section is a card with its name and count, its rows
// as cells grouped into runs by tone, most pressing first, so a section
// reads as a few coloured runs (`overviewSections`); each run's count is a
// way to its rows, worded as what they are (`TONE_WORDS`, `runLabel`).
//
// A row carries one tone (`rowTone`), the most pressing, and whether it is
// assigned to the viewer (`rowInvolve`), so the overview can find it on the
// page.
import type { DashPr } from '../api/queries';
import type { DashRow } from '../state/dashboard/rows';
import { involvement } from './involvement';
import { turn } from './stackRail';
import { checkState } from './stackState';

/** A row's colour on the overview: the viewer's turn, failing checks, waiting on others, a draft, closed, or none of those (nobody's turn, or not known). */
export type MapTone = 'you' | 'failing' | 'others' | 'draft' | 'closed' | 'none';

const failing = (pr: DashPr) => checkState(pr.commits.nodes[0]?.commit.statusCheckRollup?.state) === 'failing';

/** `row`'s tone on the overview. */
export function rowTone(row: DashRow, me: string): MapTone {
  const pr = row.pr;
  if (pr.state !== 'OPEN') return 'closed';
  const t = turn(row.attention, pr.author?.login ?? null, me).kind;
  if (t === 'you') return 'you';
  if (failing(pr)) return 'failing';
  if (pr.isDraft) return 'draft';
  if (t === 'others' || t === 'author') return 'others';
  return 'none';
}

/** How a row's cell is capped on the overview: assigned with a review asked (their turn), assigned only, or neither. */
export type MapInvolve = 'both' | 'assigned';

/** `row`'s cap on the overview; undefined for the viewer's own, or one not assigned to them. */
export function rowInvolve(row: DashRow, me: string): MapInvolve | undefined {
  const inv = involvement(row.pr, me);
  if (!inv?.assigned) return undefined;
  return inv.requested ? 'both' : 'assigned';
}

/** The strongest of several caps (a folded stack's, from its members'). */
export function topInvolve(caps: readonly (MapInvolve | undefined)[]): MapInvolve | undefined {
  return caps.includes('both') ? 'both' : caps.includes('assigned') ? 'assigned' : undefined;
}

/** The most pressing of several tones (a folded stack's, from its members'). */
export function topTone(tones: readonly MapTone[]): MapTone {
  const order: MapTone[] = ['you', 'failing', 'others', 'draft', 'none', 'closed'];
  return order.find((t) => tones.includes(t)) ?? 'none';
}

/**
 * Which of the targets at page offsets `tops` (in page order) a link goes
 * to next: the first below `line` (the one it went to last, or the top of
 * the window), else the first, round again; -1 for none.
 */
export function nextTarget(tops: readonly number[], line: number): number {
  if (!tops.length) return -1;
  const i = tops.findIndex((t) => t > line + 1);
  return i < 0 ? 0 : i;
}

// ------------------------------------------------------------- the overview

/** A row as the overview draws it: where it is in page order (across the sections), its tone, its cap, and what hovering it says. */
export interface OverviewCell {
  index: number;
  tone: MapTone;
  involve: MapInvolve | null;
  title: string;
}
export interface OverviewSection {
  title: string;
  cells: readonly OverviewCell[];
  /** Its rows are still loading, or some are being checked again: its card is still counting (logic/pending). */
  counting?: boolean;
}
/** A section's rows of one tone, in page order. */
export interface OverviewRun {
  tone: MapTone;
  cells: OverviewCell[];
}
export interface OverviewCard {
  title: string;
  count: number;
  /** Still counting: the section's rows are loading or being checked again. */
  counting: boolean;
  /** Its runs, most pressing tone first. */
  runs: OverviewRun[];
}

/** What a run of each tone is called: a word beside its count, and what it means. */
export const TONE_WORDS: Readonly<Record<MapTone, { word: string; means: string }>> = {
  you: { word: 'your turn', means: 'your turn' },
  failing: { word: 'failing', means: 'checks failing' },
  others: { word: 'waiting', means: 'waiting on someone else' },
  draft: { word: 'draft', means: 'drafts' },
  none: { word: 'idle', means: 'nobody’s turn, or not known' },
  closed: { word: 'closed', means: 'closed or merged' },
};

/** A run's count as a control: "12 your turn in Needs your attention (your turn): go to the next". */
export function runLabel(count: number, tone: MapTone, section: string): string {
  const { word, means } = TONE_WORDS[tone];
  return `${count} ${word} in ${section}${means === word ? '' : ` (${means})`}: go to the next`;
}

/** The tones, most pressing first: the order a section's runs are drawn in. */
export const TONE_ORDER: readonly MapTone[] = ['you', 'failing', 'others', 'draft', 'none', 'closed'];

/**
 * The overview's cards for `sections` in page order, those with no rows
 * left out: each its rows grouped by tone into runs, most pressing first
 * (TONE_ORDER), each run's rows kept in page order.
 */
export function overviewSections(sections: readonly OverviewSection[]): OverviewCard[] {
  return sections
    .filter((s) => s.cells.length)
    .map((s) => ({
      title: s.title,
      count: s.cells.length,
      counting: !!s.counting,
      runs: TONE_ORDER.flatMap((tone) => {
        const cells = s.cells.filter((c) => c.tone === tone);
        return cells.length ? [{ tone, cells }] : [];
      }),
    }));
}
